import { defineStore } from 'pinia'
import themeApi from '@/api/modules/theme'
import { changeLocale } from '@/global/i18n'
import claitonLogo from '@/assets/images/claiton-logo.png'
import lfLogo from '@/assets/themes/lf/lf-stockholm.svg'
import svedeaLogo from '@/assets/themes/svedea/svedea_logo_white.svg'
import gjensidigeLogo from '@/assets/themes/gjensidige/gjensidige.svg'

// Predefined theme configuration for leasing company
const LEASING_THEME = {
  name: 'Claiton',
  logo: claitonLogo,
  favicons: [],
  fontFamily: 'Inter, system-ui, sans-serif',
  fontSize: '16px',
  fontWeight: '400',
  pageTextColor: '#101010',
  titleColor: '#000000',
  elementTextColor: '#404040',
  pageBackgroundColor: '#ffffff',
  menuHeight: '64px',
  menuBackgroundColor: '#ffffff',
  menuVariant: 'left',
  menuTextColor: '#ffffff',
  logoVariant: 'default',
  logoType: 'image',
  primaryColor: '#2c7cf2',
  secondaryColor: '#193e9c',
  accentColor: '#eefd3e',
  buttonPadding: '12px 24px',
  buttonVariant: 'center',
  buttonTextColor: '#ffffff',
  successColor: '#10b981',
  failureColor: '#ef4444',
  stepTextColor: '#6b7280',
  activeStepTextColor: '#111827',
  cardBackgroundColor: '#ffffff',
  starColor: '#f59e0b',
  countryId: null,
  currencyId: null,
  languageId: null,
}

// Predefined theme configuration for lf company
const LF_THEME = {
  name: 'LF',
  logo: lfLogo,
  favicons: [],
  fontFamily: 'Inter, system-ui, sans-serif',
  fontSize: '16px',
  fontWeight: '400',
  pageTextColor: '#292929',
  titleColor: '#1a1a1a',
  elementTextColor: '#404040',
  pageBackgroundColor: '#ffffff',
  menuHeight: '64px',
  menuBackgroundColor: '#ffffff',
  menuVariant: 'left',
  menuTextColor: '#ffffff',
  logoVariant: 'default',
  logoType: 'image',
  primaryColor: '#005aa0',
  secondaryColor: '#003d72',
  accentColor: '#e3f4fc',
  buttonPadding: '12px 24px',
  buttonVariant: 'center',
  buttonTextColor: '#ffffff',
  successColor: '#10b981',
  failureColor: '#ef4444',
  stepTextColor: '#6b7280',
  activeStepTextColor: '#111827',
  cardBackgroundColor: '#ffffff',
  starColor: '#f59e0b',
  countryId: null,
  currencyId: null,
  languageId: null,
}

// Predefined theme configuration for svedea company
const SVEDEA_THEME = {
  name: 'Svedea',
  logo: svedeaLogo,
  favicons: [],
  fontFamily: 'Inter, system-ui, sans-serif',
  fontSize: '16px',
  fontWeight: '400',
  pageTextColor: '#404040',
  titleColor: '#2a2a2a',
  elementTextColor: '#606060',
  pageBackgroundColor: '#ffffff',
  menuHeight: '64px',
  menuBackgroundColor: '#005073',
  menuVariant: 'left',
  menuTextColor: '#ffffff',
  logoVariant: 'default',
  logoType: 'image',
  primaryColor: '#91cfc9',
  secondaryColor: '#005073',
  accentColor: '#bde2de',
  buttonPadding: '12px 24px',
  buttonVariant: 'center',
  buttonTextColor: '#ffffff',
  successColor: '#10b981',
  failureColor: '#ef4444',
  stepTextColor: '#6b7280',
  activeStepTextColor: '#111827',
  cardBackgroundColor: '#ffffff',
  starColor: '#f59e0b',
  countryId: null,
  currencyId: null,
  languageId: null,
}

// Predefined theme configuration for Gjensidige company
const GJENSIDIGE_THEME = {
  name: 'Gjensidige',
  logo: gjensidigeLogo,
  favicons: [],
  fontFamily: 'Inter, system-ui, sans-serif',
  fontSize: '16px',
  fontWeight: '400',
  pageTextColor: '#090c33',
  titleColor: '#050619',
  elementTextColor: '#2a2d4d',
  pageBackgroundColor: '#ffffff',
  menuHeight: '64px',
  menuBackgroundColor: '#ffffff',
  menuVariant: 'left',
  menuTextColor: '#ffffff',
  logoVariant: 'default',
  logoType: 'image',
  primaryColor: '#7c55ff',
  secondaryColor: '#090c33',
  accentColor: '#f9ffd7',
  buttonPadding: '12px 24px',
  buttonVariant: 'center',
  buttonTextColor: '#ffffff',
  successColor: '#10b981',
  failureColor: '#ef4444',
  stepTextColor: '#6b7280',
  activeStepTextColor: '#111827',
  cardBackgroundColor: '#ffffff',
  starColor: '#f59e0b',
  countryId: null,
  currencyId: null,
  languageId: null,
}

export const useThemeStore = defineStore('theme', {
  state: () => ({
    currentCompany: null,
    currentTheme: {
      name: '',
      logo: null,
      favicons: [],
      fontFamily: 'Inter, system-ui, sans-serif',
      fontSize: '16px',
      fontWeight: '400',
      pageTextColor: '#1f2937',
      titleColor: '#111827',
      elementTextColor: '#374151',
      pageBackgroundColor: '#ffffff',
      menuHeight: '64px',
      menuBackgroundColor: '#ffffff',
      menuVariant: 'left',
      menuTextColor: '#ffffff',
      logoVariant: 'default',
      logoType: 'image',
      primaryColor: '#3b82f6',
      secondaryColor: '#93c5fd',
      accentColor: '#1d4ed8',
      buttonPadding: '12px 24px',
      buttonVariant: 'center',
      buttonTextColor: '#ffffff',
      successColor: '#10b981',
      failureColor: '#ef4444',
      stepTextColor: '#6b7280',
      activeStepTextColor: '#111827',
      cardBackgroundColor: '#ffffff',
      starColor: '#f59e0b',
      countryId: null,
      currencyId: null,
      languageId: null,
    },
    isLoading: false,
    initialLoadComplete: false,
  }),

  getters: {
    countryId: (state) => state.currentTheme?.countryId ?? null,
    currencyId: (state) => state.currentTheme?.currencyId ?? null,
    languageId: (state) => state.currentTheme?.languageId ?? null,
  },

  actions: {
    /**
     * Load and apply theme for a specific company
     * @param {string} companyId - The company identifier (e.g., 'company') or 'leasing'/'lf'/'svedea'/'gjensidige' for predefined themes
     * @returns {Promise} Promise that resolves when theme is applied
     */
    async loadAndApplyTheme(companyId) {
      this.isLoading = true
      this.currentCompany = companyId

      try {
        // Check if this is the special 'leasing' company
        if (companyId === 'leasing') {
          // Use predefined Claiton theme for leasing
          this.currentTheme = { ...LEASING_THEME }

          // Apply the theme to the UI
          this.applyTheme()
          this.isLoading = false
          return
        }

        // Check if this is the special 'lf' company
        if (companyId === 'lf') {
          // Use predefined LF theme
          this.currentTheme = { ...LF_THEME }

          // Apply the theme to the UI
          this.applyTheme()
          this.isLoading = false
          return
        }

        // Check if this is the special 'svedea' company
        if (companyId === 'svedea') {
          // Use predefined Svedea theme
          this.currentTheme = { ...SVEDEA_THEME }

          // Apply the theme to the UI
          this.applyTheme()
          this.isLoading = false
          return
        }

        // Check if this is the special 'gjensidige' company
        if (companyId === 'gjensidige') {
          // Use predefined Gjensidige theme
          this.currentTheme = { ...GJENSIDIGE_THEME }

          // Apply the theme to the UI
          this.applyTheme()
          this.isLoading = false
          return
        }

        // Try to get theme from API for other companies
        const themeConfig = await themeApi.getCompanyTheme(companyId)

        if (themeConfig) {
          // API returned a theme, use it
          this.currentTheme = {
            name: themeConfig.name || companyId,
            logo: themeConfig.logo || null,
            favicons: themeConfig.favicons || [],
            fontFamily: themeConfig.fontFamily || this.currentTheme.fontFamily,
            fontSize: themeConfig.fontSize || this.currentTheme.fontSize,
            fontWeight: themeConfig.fontWeight || this.currentTheme.fontWeight,
            pageTextColor: themeConfig.pageTextColor || this.currentTheme.pageTextColor,
            titleColor: themeConfig.titleColor || this.currentTheme.titleColor,
            elementTextColor: themeConfig.elementTextColor || this.currentTheme.elementTextColor,
            pageBackgroundColor:
              themeConfig.pageBackgroundColor || this.currentTheme.pageBackgroundColor,
            menuHeight: themeConfig.menuHeight || this.currentTheme.menuHeight,
            menuBackgroundColor:
              themeConfig.menuBackgroundColor || this.currentTheme.menuBackgroundColor,
            menuVariant: themeConfig.menuVariant || this.currentTheme.menuVariant,
            menuTextColor: themeConfig.menuTextColor || this.currentTheme.menuTextColor,
            logoVariant: themeConfig.logoVariant || this.currentTheme.logoVariant,
            logoType: themeConfig.logoType || this.currentTheme.logoType,
            primaryColor: themeConfig.primaryColor || this.currentTheme.primaryColor,
            secondaryColor: themeConfig.secondaryColor || this.currentTheme.secondaryColor,
            accentColor: themeConfig.accentColor || this.currentTheme.accentColor,
            buttonPadding: themeConfig.buttonPadding || this.currentTheme.buttonPadding,
            buttonVariant: themeConfig.buttonVariant || this.currentTheme.buttonVariant,
            buttonTextColor: themeConfig.buttonTextColor || this.currentTheme.buttonTextColor,
            successColor: themeConfig.successColor || this.currentTheme.successColor,
            failureColor: themeConfig.failureColor || this.currentTheme.failureColor,
            stepTextColor: themeConfig.stepTextColor || this.currentTheme.stepTextColor,
            activeStepTextColor:
              themeConfig.activeStepTextColor || this.currentTheme.activeStepTextColor,
            cardBackgroundColor:
              themeConfig.cardBackgroundColor || this.currentTheme.cardBackgroundColor,
            starColor: themeConfig.starColor || this.currentTheme.starColor,
            countryId: themeConfig.countryId ?? null,
            currencyId: themeConfig.currencyId ?? null,
            languageId: themeConfig.languageId ?? null,
          }

          // Update favicons in the document head
          this.updateFavicons()

          // Set locale based on languageId from theme
          if (themeConfig.languageId) {
            await changeLocale(themeConfig.languageId)
          }
        } else {
          // API failed or returned null, use default theme
          console.log(`Using default theme for ${companyId} (API unavailable)`)
        }

        // Apply the theme to the UI
        this.applyTheme()
      } catch (error) {
        // Re-throw 404 errors to let router handle invalid companies
        if (error.response?.status === 404) {
          throw error
        }
        // For other errors, log and continue with default theme
        console.error(`Error loading theme for ${companyId}:`, error)
        this.resetToDefault(true)
      } finally {
        this.isLoading = false
      }
    },

    /**
     * Apply the current theme to CSS custom properties
     */
    applyTheme() {
      const root = document.documentElement

      // Apply font settings
      root.style.setProperty('--theme-font-family', this.currentTheme.fontFamily)
      root.style.setProperty('--theme-font-size', this.currentTheme.fontSize)
      root.style.setProperty('--theme-font-weight', this.currentTheme.fontWeight)

      // Apply color settings
      root.style.setProperty('--theme-page-text-color', this.currentTheme.pageTextColor)
      root.style.setProperty('--theme-title-color', this.currentTheme.titleColor)
      root.style.setProperty('--theme-element-text-color', this.currentTheme.elementTextColor)
      root.style.setProperty('--theme-page-background-color', this.currentTheme.pageBackgroundColor)
      root.style.setProperty('--theme-primary-color', this.currentTheme.primaryColor)
      root.style.setProperty('--theme-secondary-color', this.currentTheme.secondaryColor)
      root.style.setProperty('--theme-accent-color', this.currentTheme.accentColor)
      root.style.setProperty('--theme-success-color', this.currentTheme.successColor)
      root.style.setProperty('--theme-failure-color', this.currentTheme.failureColor)

      // Apply menu settings
      root.style.setProperty('--theme-menu-height', this.currentTheme.menuHeight)
      root.style.setProperty('--theme-menu-background-color', this.currentTheme.menuBackgroundColor)
      root.style.setProperty('--theme-menu-text-color', this.currentTheme.menuTextColor)

      // Apply button settings
      root.style.setProperty('--theme-button-padding', this.currentTheme.buttonPadding)
      root.style.setProperty('--theme-button-text-color', this.currentTheme.buttonTextColor)

      // Apply step settings
      root.style.setProperty('--theme-step-text-color', this.currentTheme.stepTextColor)
      root.style.setProperty(
        '--theme-active-step-text-color',
        this.currentTheme.activeStepTextColor,
      )

      // Apply card settings
      root.style.setProperty('--theme-card-background-color', this.currentTheme.cardBackgroundColor)

      // Apply star color
      root.style.setProperty('--theme-star-color', this.currentTheme.starColor)

      // Apply theme class to body
      document.body.className = document.body.className.replace(/theme-\w+/g, '')
      if (this.currentCompany) {
        document.body.classList.add(`theme-${this.currentCompany}`)
      }
      this.initialLoadComplete = true
    },

    /**
     * Update favicons in the document head
     */
    updateFavicons() {
      if (!this.currentTheme.favicons || this.currentTheme.favicons.length === 0) {
        return
      }

      // Remove existing favicons
      const existingFavicons = document.head.querySelectorAll('link[rel*="icon"]')
      existingFavicons.forEach((link) => link.remove())

      // Add new favicons
      this.currentTheme.favicons.forEach((faviconUrl, index) => {
        const link = document.createElement('link')
        link.rel = index === 0 ? 'icon' : 'apple-touch-icon'
        link.href = faviconUrl

        // Try to determine size from URL or default sizes
        if (faviconUrl.includes('32x32')) {
          link.sizes = '32x32'
        } else if (faviconUrl.includes('64x64')) {
          link.sizes = '64x64'
        } else if (faviconUrl.includes('16x16')) {
          link.sizes = '16x16'
        }

        document.head.appendChild(link)
      })
    },

    /**
     * Reset to default theme
     * @param {boolean} keepCurrentCompany - If true, preserves the current company value
     */
    resetToDefault(keepCurrentCompany = false) {
      if (!keepCurrentCompany) this.currentCompany = null
      this.currentTheme = {
        name: '',
        logo: null,
        favicons: [],
        fontFamily: 'Inter, system-ui, sans-serif',
        fontSize: '16px',
        fontWeight: '400',
        pageTextColor: '#1f2937',
        titleColor: '#111827',
        elementTextColor: '#374151',
        pageBackgroundColor: '#ffffff',
        menuHeight: '64px',
        menuBackgroundColor: '#ffffff',
        menuVariant: 'left',
        menuTextColor: '#ffffff',
        logoVariant: 'default',
        logoType: 'image',
        primaryColor: '#3b82f6',
        secondaryColor: '#93c5fd',
        accentColor: '#1d4ed8',
        buttonPadding: '12px 24px',
        buttonVariant: 'center',
        buttonTextColor: '#ffffff',
        successColor: '#10b981',
        failureColor: '#ef4444',
        stepTextColor: '#6b7280',
        activeStepTextColor: '#111827',
        cardBackgroundColor: '#ffffff',
        starColor: '#f59e0b',
        countryId: null,
        currencyId: null,
        languageId: null,
      }
      this.applyTheme()
      this.initialLoadComplete = true
    },
  },
})
